import {
  Card,
  Checkbox,
  CodeViewer,
  Divider,
  Flex,
  Input,
  SecureCopy,
  SimpleTable,
  Textarea,
  Typography,
} from "djuno-design";
import { ReactComponent as FaceSmile } from "./../../assets/icons/face-smile.svg";
import ExpandableCard from "../../components/ExpandableCard";

const { Title, Text, Paragraph } = Typography;

const CopyablePage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Title level={4}>Copyable</Title>
        <Text size="sm" style={{ marginBottom: 30 }}>
          The{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            copyable
          </Typography.Link>{" "}
          prop enhances user interaction by enabling easy copying of text and
          code across various components, including{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            CodeViewer
          </Typography.Link>
          ,{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Input
          </Typography.Link>
          ,{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Textarea
          </Typography.Link>
          ,{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Paragraph
          </Typography.Link>
          , and{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Text
          </Typography.Link>
          . In the{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            CodeViewer
          </Typography.Link>
          , it allows copying of complex code snippets with syntax highlighting.
          The{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Input
          </Typography.Link>{" "}
          and{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Textarea
          </Typography.Link>{" "}
          components enable both static and dynamic text copying, facilitating
          quick sharing. The{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            SecureCopy
          </Typography.Link>{" "}
          component specifically manages sensitive text copying securely.
          Lastly, the{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Paragraph
          </Typography.Link>{" "}
          and{" "}
          <Typography.Link target="_blank" href="" size="xs" code>
            Text
          </Typography.Link>{" "}
          components personalize the copying experience with customizable
          tooltips and icons for an interactive interface.
        </Text>

        <Title level={4}>copyable Usage in Each Component</Title>
        <Text size="sm">
          The{" "}
          <Text size="sm" code>
            copyable
          </Text>{" "}
          prop is used across several components to enable seamless copying of
          text and code, enhancing interactivity and user experience. Below is
          an overview of how{" "}
          <Text size="sm" code>
            copyable
          </Text>{" "}
          is applied in each component:
        </Text>

        <ul className="marker:text-sky-400 list-disc pl-6">
          {/* CodeViewer */}
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                CodeViewer
              </Text>
              : In the CodeViewer component,{" "}
              <Text size="sm" code>
                copyable
              </Text>{" "}
              allows users to copy code snippets with various formatting options
              and themes. This is especially useful for copying complex or
              multi-line code blocks, with options for syntax highlighting, line
              numbers, and background transparency, making it ideal for
              documentation and code sharing.
            </Text>
          </li>

          {/* Textarea and Input Components */}
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Textarea and Input Components
              </Text>
              : The{" "}
              <Text size="sm" code>
                copyable
              </Text>{" "}
              prop in Textarea and Input components enables two methods of text
              copying:
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Static Text
                    </Text>
                    : Allows copying of predefined, static text, which is useful
                    for replicating template messages.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Dynamic Text
                    </Text>
                    : Supports copying text based on the current input value,
                    allowing users to easily share customized or user-generated
                    content.
                  </Text>
                </li>
              </ul>
            </Text>
          </li>

          {/* Typography Components */}
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Typography Components
              </Text>
              : In the Paragraph and Text components, the{" "}
              <Text size="sm" code>
                copyable
              </Text>{" "}
              prop provides quick inline text copying, with options for
              tooltips, icons, and feedback messages to improve the user
              experience. Customizations include:
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Custom Tooltips
                    </Text>
                    : Display specific messages (e.g., "click here" before
                    copying, "you clicked!!" afterward) to guide users through
                    the copy action.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Custom Icons
                    </Text>
                    : Visual indicators can be set to display before and after
                    copying, representing the copy status clearly.
                  </Text>
                </li>
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Tooltip Control
                    </Text>
                    : Tooltips can be disabled entirely for simpler interactions
                    if preferred.
                  </Text>
                </li>
              </ul>
            </Text>
          </li>

          {/* SecureCopy Component */}
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                SecureCopy
              </Text>
              : The SecureCopy component uses the{" "}
              <Text size="sm" code>
                copyable
              </Text>{" "}
              prop to enable users to securely copy sensitive information. It
              offers:
              <ul className="marker:text-sky-400 list-disc pl-6">
                <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Secure Copying
                    </Text>
                    : This allows sensitive data to be copied with enhanced
                    security measures, ensuring privacy during the copying
                    process.
                    <Text size="sm" style={{ marginTop: 5 }}></Text>
                  </Text>
                </li>
                {/* <li>
                  <Text size="sm">
                    <Text size="sm" strong>
                      Feedback Mechanism
                    </Text>
                    : Provides visual or textual feedback after copying,
                    informing users of successful actions.
                    <Text size="sm" style={{ marginTop: 5 }}>
                      <strong>Explanation:</strong> Feedback enhances user
                      confidence, confirming that their sensitive data was
                      copied successfully.
                    </Text>
                  </Text>
                </li> */}
              </ul>
            </Text>
          </li>
        </ul>

        <Flex direction="col" className="w-full mt-5 gap-5">
          {/* Textarea Examples */}
          <ExpandableCard
            title="Textarea"
            codeViewerProps={{
              code: TextareaExample,
            }}
          >
            <Text size="sm" className="font-semibold mt-10">
              By setting the copyable prop to true, this Textarea allows users
              to easily copy all text entered into it without any additional
              functionality. This makes it a straightforward tool for sharing
              any type of content input by the user.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Textarea
                label="Textarea"
                placeholder="Enter custom notes if any"
                copyable={true}
              />
            </Flex>
            <Text size="sm" className="font-semibold mt-10">
              In this example, the copyable prop is set as a function that takes
              the current value of the Textarea and prepends "Hi" to it. This
              means when users copy the text, they will receive the text
              prefixed with "Hi," making it easy to greet or personalize the
              copied content.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Textarea
                label="Textarea"
                placeholder="Enter custom notes if any"
                copyable={{
                  text: ({ value }) => `Hi ${value}`,
                }}
              />
            </Flex>

            <Text size="sm" className="font-semibold mt-10">
              In this example, the copyable prop is configured to allow for a
              custom copy icon and tooltips. The icons array specifies two
              different icons: one default and one with a green color indicating
              a successful copy. The tooltips array provides two messages—one
              displayed before copying ("Click to copy") and another after the
              text has been successfully copied ("Text copied!"). This
              customization enhances user feedback and interaction when using
              the textarea.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Textarea
                label="Textarea"
                placeholder="Enter custom notes if any"
                copyable={{
                  icons: [
                    <FaceSmile />,
                    <FaceSmile className="text-green-500" />,
                  ],
                  tooltips: ["Click to copy", "Text copied!"],
                }}
              />
            </Flex>
            <Text size="sm" className="font-semibold mt-10">
              In this example, the copyable prop allows for tooltips to be
              hidden by setting the tooltips property to false. This means that
              when users attempt to copy text from the textarea, no tooltip will
              appear to indicate the copy action. The icons are still present,
              providing a visual cue of the copy functionality, but without
              additional text feedback. This can be useful in scenarios where a
              cleaner UI is desired or when tooltips may be deemed unnecessary.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Textarea
                label="Textarea"
                placeholder="Enter custom notes if any"
                copyable={{
                  icons: [
                    <FaceSmile />,
                    <FaceSmile className="text-green-500" />,
                  ],
                  tooltips: false,
                }}
              />
            </Flex>
          </ExpandableCard>

          {/* Input Examples */}
          <ExpandableCard
            title="Input "
            codeViewerProps={{
              code: InputExample,
            }}
          >
            <Text size="sm" className="font-semibold mt-10">
              In this example, the Input component is configured to copy a
              custom message when the copy action is triggered. The text
              property of thecopyable prop is a function that takes the current
              input value "v" and prepends "Hi " to it. Therefore, if the user
              input is "djuno-design," the copied text will be "Hi
              djuno-design." This functionality provides a way to modify the
              content that gets copied to the clipboard, enhancing user
              interaction by allowing for contextual messaging.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Input
                label="copyable"
                copyable={{
                  text: (v) => `Hi ${v.value || ""}`,
                }}
                value="djuno-design"
              />
            </Flex>

            <Text size="sm" className="font-semibold mt-10">
              This example demonstrates the use of custom icons and tooltips for
              the copy action. The copyable prop can be further customized to
              include icons and tooltips . Here, two smiley face icons are
              provided: one in the default state and one in green to indicate a
              successful copy. The tooltips property contains messages that
              appear when the user hovers over the icons, enhancing feedback.
              The first tooltip ("Click to copy") encourages interaction, while
              the second ("Text copied!") confirms that the action was
              successful. This combination of visual and textual feedback
              enhances user experience by clearly indicating both the action and
              its result.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Input
                label="Input"
                copyable={{
                  icons: [
                    <FaceSmile />,
                    <FaceSmile className="text-green-500" />,
                  ],
                  tooltips: ["Click to copy", "Text copied!"],
                }}
              />
            </Flex>
          </ExpandableCard>

          {/* SecureCopy Examples */}
          <ExpandableCard
            title="SecureCopy"
            codeViewerProps={{
              code: SecureCopyExample,
            }}
          >
            <Text size="sm" className="font-semibold mt-10">
              In this example, the SecureCopy component is set up to provide a
              tooltip feedback mechanism. The copyable prop contains tooltips,
              which display "copy" when the user can perform the action and
              "copyed" once the text has been successfully copied. The text
              property is set to an empty string, indicating that no specific
              text is being copied, thus keeping the interaction minimal. The
              type "hide" ensures that the copied text is not displayed in the
              UI, making it suitable for cases where the content should remain
              discreet.
            </Text>

            <Flex items="end" className="gap-3 w-full flex py-5">
              <SecureCopy
                text="small"
                type="hide"
                uiSize="small"
                copyable={{ tooltips: ["copy", "copyed"], text: "" }}
              />
            </Flex>

            <Text size="sm" className="font-semibold mt-10">
              This example illustrates how the SecureCopy component can be
              utilized to copy predefined text. Here, the copyable prop includes
              tooltips for user feedback and a specific text ("text to copy")
              that will be copied to the clipboard when the user triggers the
              copy action. This configuration allows for a clear and defined
              interaction where the user is informed about the action, and the
              specified text is copied to the clipboard successfully. The type
              "hide" still ensures that the text itself remains invisible in the
              UI while the copy action operates seamlessly.
            </Text>

            <Flex items="end" className="gap-3 w-full flex py-5">
              <SecureCopy
                text="small"
                type="hide"
                uiSize="small"
                copyable={{
                  tooltips: ["copy", "copyed"],
                  text: "text to copy",
                }}
              />
            </Flex>
          </ExpandableCard>

          {/* CodeViewer Examples */}
          <ExpandableCard
            title="CodeViewer"
            codeViewerProps={{
              code: CodeViewerExample,
            }}
          >
            <Text size="sm" className="font-semibold mt-10">
              In this example,By enabling the copyable prop, users can easily
              copy this command to their clipboard with a single action,
              enhancing their workflow and reducing friction when integrating
              the code into their own development environment.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <CodeViewer
                theme="dark"
                language="shell"
                copyable
                code={`npm install djuno-design --save`}
              />
            </Flex>
          </ExpandableCard>

          {/* Typography Copyable Examples */}
          <ExpandableCard
            title="Typography"
            description="copyable options"
            codeViewerProps={{
              code: TypographyExample,
            }}
          >
            <Text size="sm" className="font-semibold mt-10">
              In this example, the Paragraph component uses the copyable prop to
              allow users to copy "This is a copyable text." directly,
              simplifying the text-copying process.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Paragraph copyable>This is a copyable text.</Paragraph>
            </Flex>

            <Text size="sm" className="font-semibold mt-10">
              Here, the Text component is configured to copy "Hello, Djuno
              Design!" instead of the visible text when the copy action is
              triggered, allowing for customized clipboard content.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Text copyable={{ text: "Hello, Djuno Design!" }}>
                Replace copy text.
              </Text>
            </Flex>

            <Text size="sm" className="font-semibold mt-10">
              This example shows the use of custom tooltips and icons in the{" "}
              Text component. The tooltips offer feedback during the copy
              action, while the icons indicate the copy state, enhancing user
              interaction.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Text
                copyable={{
                  tooltips: ["click here", "you clicked!!"],
                  icons: [
                    <FaceSmile />, // Default icon
                    <FaceSmile className="text-green-500" />, // Success icon
                  ],
                }}
              >
                Custom Copy icon and replace tooltips text.
              </Text>
            </Flex>

            <Text size="sm" className="font-semibold mt-10">
              This example demonstrates how to hide tooltips by setting tooltips
              tofalse. Users can still copy the text without receiving tooltip
              feedback, keeping the interface clean.
            </Text>
            <Flex className="gap-5 w-full py-5">
              <Text copyable={{ tooltips: false }}>Hide Copy tooltips.</Text>
            </Flex>
          </ExpandableCard>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5}>API</Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* CopyableText */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">CopyableText</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the text that can be copied. Can include a
                        string, number, null, or undefined value.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | number | null | undefined
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* CopyableProp */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">CopyableProp</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines whether the copyable feature is enabled or
                        includes options for copying.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean | CopyableOptionsProp
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* CopyableTextOption */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">CopyableTextOption</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Provides text to be copied. Can be a static string or a
                        function that returns text based on the provided prop
                        values.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {` string | (prop: {{ value: string | null, element?: HTMLElement | null }}) => CopyableText`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* CopyableOptionsProp */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">CopyableOptionsProp</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Options for customizing copyable behavior, including
                        text, icons, and tooltip settings.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {"{"} text: CopyableTextOption, icon?: ReactNode | {"{"}
                        icon: ReactNode, status: boolean {"}"}, tooltip?:
                        {"{"} content: TooltipText, disable: boolean {"}"} {"}"}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const TextareaExample = `<Flex direction="col" className="gap-4 w-full">
  <ExpandableCard
    codeViewerProps={{
      code: TextareaExample1
    }}
  >
    <Text size="sm" className="font-semibold mt-10">
      Copyable without function:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        copyable={true}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Copyable with function:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
            copyable={(v) => \`Hi \${v}\`}
      />
    </Flex>  
    <Text size="sm" className="font-semibold mt-10">
      Custom Copy icon and replace tooltips text:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        copyable={{
          icon: [
            <FaceSmile />,
            <FaceSmile className="text-green-500" />,
          ],
          tooltips: ["Click to copy", "Text copied!"],
        }}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Hide Copy tooltips:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        copyable={{
          icon: [
            <FaceSmile />,
            <FaceSmile className="text-green-500" />,
          ],
          tooltips: false,
        }}
      />
    </Flex>
  </ExpandableCard>
</Flex>`;

const InputExample = `<Flex direction="col" className="gap-5 w-full">
  <Flex items="end" className="gap-3 w-full flex ">
    <Input
      label="copyable"
            copyable={(v) => \`Hi \${v}\`}
      value="djuno-design"
    />
  </Flex> 
  <Flex items="end" className="gap-3 w-full flex ">
    <Input
      label="Input"
      copyable={{
        icon: [
          <FaceSmile />,
          <FaceSmile className="text-green-500" />,
        ],
        tooltips: ["Click to copy", "Text copied!"],
      }}
    />
  </Flex>
</Flex>`;
const SecureCopyExample = `
<Flex direction="col" className="gap-5 w-full ">
<Flex items="end" className="gap-3 w-full flex ">
<Flex items="end" className="gap-3 w-full flex ">
    <SecureCopy
        text="small"
        type="hide"
        uiSize="small"
        copyable={{ tooltips: ["copy", "copyed"], text: "" }}
    />
    </Flex>
    <Flex items="end" className="gap-3 w-full flex ">
    <SecureCopy
        text="small"
        type="hide"
        uiSize="small"
        copyable={{
        tooltips: ["copy", "copyed"],
        text: "text to copy",
        }}
    />
    </Flex>
</Flex>
</Flex>`;

const CodeViewerExample = `<CodeViewer
  theme="dark"
  language="shell"
  copyable
  code="npm install djuno-design --save"
/>`;

const TypographyExample = `<Flex direction={"col"} className="gap-3 w-full">
  <Paragraph copyable>This is a copyable text.</Paragraph>
  <Text copyable={{ text: "Hello, Djuno Design!" }}>
    Replace copy text.
  </Text>
  <Text
    copyable={{
      tooltips: ["click here", "you clicked!!"],
      icon: [
        <FaceSmile />,
        <FaceSmile className="text-green-500" />,
      ],
    }}
  >
    Custom Copy icon and replace tooltips text.
  </Text>
  <Text copyable={{ tooltips: false }}>Hide Copy tooltips.</Text>
</Flex>`;

export default CopyablePage;
